'use client'

import { useEffect, useRef } from 'react'

/**
 * Hook to track the previous value of a string
 * @param key - The sessionStorage key to use for persistence (only used when persistent is true)
 * @param currentValue - The current value to track
 * @param persistent - Whether to persist the value in sessionStorage (default: true)
 * @returns previous value
 */
export const usePrevious = (
  key: string,
  currentValue: string,
  persistent = true
) => {
  const previousValueRef = useRef<string | null>(
    // Initialize with value from sessionStorage only if persistent
    persistent && typeof window !== 'undefined'
      ? sessionStorage.getItem(key)
      : null
  )

  // Track value changes and update sessionStorage
  useEffect(() => {
    const hasChanged = currentValue !== previousValueRef.current

    if (hasChanged) {
      // Update sessionStorage with current value only if persistent
      if (persistent && typeof window !== 'undefined') {
        sessionStorage.setItem(key, currentValue)
      }

      // Update previous value ref (no re-render)
      previousValueRef.current = currentValue
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue])

  return previousValueRef.current
}
